import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/features/auth/AuthProvider";
import { queryKeys } from "@/shared/lib/queryKeys";
import { logger } from "@/shared/lib/logger";

/**
 * Lazy admin-role check. Runs only after `useAuth()` has resolved a user.
 *
 * Returns `{ isAdmin, loading }` where `loading` is true while either
 * - the auth provider is still hydrating, OR
 * - the role query is in flight for the first time.
 *
 * Cached at the React Query layer so multiple consumers share one network call.
 * Stays fresh for 5 minutes — admin role rarely changes mid-session.
 */
export const useAdminRole = () => {
  const { user, loading: authLoading } = useAuth();

  const query = useQuery({
    queryKey: queryKeys.auth.adminRole(user?.id),
    enabled: !!user?.id && !authLoading,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: user!.id,
        _role: "admin",
      });
      if (error) {
        logger.error("[useAdminRole] RPC error:", error);
        throw error;
      }
      return !!data;
    },
  });

  return {
    isAdmin: query.data === true,
    loading: authLoading || (!!user?.id && query.isPending),
    error: query.error,
  };
};
